/*
 * Decides WHEN the newsletter popup opens. The modal behaviour itself (focus trap,
 * ESC, backdrop click, scroll lock) comes from Dawn's <modal-dialog> in global.js.
 *
 * Notes:
 * - <modal-dialog> moves itself to document.body in its connectedCallback, so the
 *   modal may no longer be a descendant of <newsletter-popup> by the time we look
 *   for it. We resolve it by id as well, and wait for the element to be defined
 *   before calling show().
 * - Every localStorage access is wrapped: it throws outright in some privacy modes.
 *   On failure we fail open (show the popup) rather than silently never showing it.
 */
class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.storageKey = this.dataset.storageKey || 'hawa:newsletter-popup';
    this.modal = this.querySelector('modal-dialog') || document.getElementById(this.dataset.modalId);
    if (!this.modal) return;

    // Shopify redirects back with ?customer_posted=true after a successful signup.
    const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    if (posted) {
      this.remember();
      this.open();
      return;
    }

    // Always open in the theme editor so it can be styled without clearing storage.
    if (window.Shopify && window.Shopify.designMode) {
      this.open();
      return;
    }

    if (!this.shouldShow()) return;

    const delay = parseFloat(this.dataset.delay);
    window.setTimeout(() => this.open(), (Number.isFinite(delay) ? delay : 5) * 1000);
  }

  open() {
    window.customElements.whenDefined('modal-dialog').then(() => {
      if (typeof this.modal.show !== 'function') return;
      this.modal.show(document.activeElement);
      this.watchForClose();
    });
  }

  // Dawn's hide() just removes the [open] attribute, so that is what we watch.
  watchForClose() {
    const observer = new MutationObserver(() => {
      if (!this.modal.hasAttribute('open')) {
        this.remember();
        observer.disconnect();
      }
    });
    observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
  }

  shouldShow() {
    const days = parseFloat(this.dataset.redisplayDays);
    let stamp;
    try {
      stamp = window.localStorage.getItem(this.storageKey);
    } catch (error) {
      return true;
    }
    if (!stamp) return true;

    const dismissedAt = parseInt(stamp, 10);
    if (!Number.isFinite(dismissedAt)) return true;
    if (!Number.isFinite(days) || days <= 0) return false;

    return Date.now() - dismissedAt > days * 24 * 60 * 60 * 1000;
  }

  remember() {
    try {
      window.localStorage.setItem(this.storageKey, String(Date.now()));
    } catch (error) {
      // Storage unavailable - the popup will show again next visit. Acceptable.
    }
  }
}

if (!window.customElements.get('newsletter-popup')) {
  window.customElements.define('newsletter-popup', NewsletterPopup);
}
